import React, { useMemo } from 'react';
import { Leaf, Download, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Banner shown at top of Biometrical / other target pages when PhenoScan hands off.
 *
 * Supports two hand-off shapes:
 *   1. Single-run (legacy):  handoff.traits = {trait1: v, trait2: v, ...}
 *   2. Integrated matrix:    handoff.traits = [{sample_id, trait1, ...}, ...]
 */
export default function PhenoScanHandoffBanner({ handoff, targetLabel }) {
  const info = useMemo(() => analyze(handoff), [handoff]);
  if (!handoff) return null;

  const downloadCsv = () => {
    const rows = Array.isArray(handoff.traits) ? handoff.traits : [handoff.traits];
    const cols = Object.keys(rows[0] || {});
    const header = cols.join(',');
    const body = rows.map(r => cols.map(c => {
      const v = r[c];
      if (v === null || v === undefined) return '';
      const s = String(v);
      return s.includes(',') ? `"${s.replace(/"/g, '""')}"` : s;
    }).join(',')).join('\n');
    const blob = new Blob([header + '\n' + body], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'phenoscan_handoff.csv';
    a.click();
    URL.revokeObjectURL(url);
    toast.success('CSV downloaded');
  };

  const warning = buildModuleWarning(handoff, info);
  const good = warning === null;

  return (
    <div className="rounded-xl p-4 mb-4"
      style={{
        background: good ? '#ECFDF5' : '#FFFBEB',
        border: `1px solid ${good ? '#A7F3D0' : '#FDE68A'}`
      }}
      data-testid="phenoscan-handoff-banner">
      <div className="flex items-start gap-3">
        {good
          ? <CheckCircle2 size={20} style={{ color: '#065F46', flexShrink: 0, marginTop: 2 }} />
          : <Leaf size={20} style={{ color: '#065F46', flexShrink: 0, marginTop: 2 }} />}
        <div className="flex-1">
          <div className="font-semibold mb-1" style={{ color: '#065F46' }}>
            Trait matrix received from PhenoScan → {targetLabel}
          </div>
          <div className="text-xs mb-2" style={{ color: '#4F4B44' }}>
            Source: <code>{handoff.source}</code> · {info.rowCount} sample{info.rowCount === 1 ? '' : 's'} ·
            {info.traitCount} trait{info.traitCount === 1 ? '' : 's'}
            {info.traitNames.length > 0 && (
              <>
                : {info.traitNames.slice(0, 6).join(', ')}
                {info.traitNames.length > 6 ? `… (+${info.traitNames.length - 6})` : ''}
              </>
            )}
          </div>
          {warning && (
            <div className="rounded-lg px-3 py-2 text-xs mt-2 flex gap-2 items-start"
              style={{ background: '#FEF3C7', color: '#78350F' }}
              data-testid="handoff-module-warning">
              <AlertTriangle size={13} className="flex-shrink-0 mt-0.5" />
              <span>{warning}</span>
            </div>
          )}
          <div className="mt-3 flex gap-2 items-center">
            <button onClick={downloadCsv} data-testid="handoff-download-csv"
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs"
              style={{ background: '#fff', border: '1px solid #065F46', color: '#065F46' }}>
              <Download size={11} /> Download integrated matrix CSV
            </button>
            <details className="ml-2">
              <summary className="text-xs cursor-pointer" style={{ color: '#4F4B44' }}>View data</summary>
              <pre className="text-[11px] mt-2 p-2 rounded"
                style={{ background: '#F3F6F3', color: '#1B4332', maxWidth: 600, maxHeight: 200, overflow: 'auto' }}>
                {JSON.stringify(handoff.traits, null, 2).slice(0, 4000)}
              </pre>
            </details>
          </div>
        </div>
      </div>
    </div>
  );
}

function analyze(handoff) {
  if (!handoff) return { rowCount: 0, traitCount: 0, traitNames: [] };
  const t = handoff.traits;
  if (Array.isArray(t)) {
    const rows = t;
    const firstRow = rows[0] || {};
    const traitNames = Object.keys(firstRow).filter(k => k !== 'sample_id');
    return { rowCount: rows.length, traitCount: traitNames.length, traitNames };
  }
  const traitNames = Object.keys(t || {});
  return { rowCount: 1, traitCount: traitNames.length, traitNames };
}

function buildModuleWarning(handoff, info) {
  const target = handoff.target;
  const N = info.rowCount;
  if (target === 'path_analysis') {
    if (N < 10)
      return `Path Analysis: Recommend ≥10 genotypes for stable path coefficients. Current N=${N}.`;
    return null;
  }
  if (target === 'pca') {
    if (N < 3)
      return `PCA: Need ≥3 samples. Current N=${N}.`;
    if (N < 10)
      return `PCA: Small N (${N}) — first principal component may be unstable.`;
    return null;
  }
  if (target === 'd_squared') {
    if (N < 5)
      return `D² Distance: Recommend ≥5 genotypes for meaningful clustering. Current N=${N}.`;
    return null;
  }
  if (target === 'heritability') {
    // If no replication column, flag it
    const first = Array.isArray(handoff.traits) ? handoff.traits[0] : handoff.traits;
    const hasRep = first && ('Rep' in first || 'replication' in first || 'rep' in first);
    if (!hasRep)
      return `Heritability: Requires replication (Rep column). Integrated matrix appears to be genotype means only — heritability will fall back to broad-sense from per-trait variance. Consider a replicated source.`;
    return null;
  }
  if (target === 'selection_index') {
    return null;   // no minimum
  }
  if (target === 'ammi' || target === 'gge_biplot') {
    // Look for environment proxy columns (env__* present, or Env column)
    const first = Array.isArray(handoff.traits) ? handoff.traits[0] : handoff.traits;
    const envCols = first ? Object.keys(first).filter(k => k.startsWith('env__') || k === 'Env' || k === 'Environment') : [];
    const lbl = target === 'ammi' ? 'AMMI' : 'GGE Biplot';
    if (envCols.length === 0)
      return `${lbl}: Requires ≥3 environments and a balanced Genotype × Environment design. Current matrix has no Env column. You may need to upload a multi-env layout separately.`;
    if (N < 10)
      return `${lbl}: Small N (${N}) — stable biplots usually need ≥10 genotypes across ≥3 environments.`;
    return null;
  }
  // Default generic warning for unknown targets
  if (N < 10)
    return `Some analyses need ≥10 replicated rows for stable estimates. Current matrix has ${N} samples.`;
  return null;
}
